import { useFormikContext } from 'formik';
import { isEmpty } from 'lodash';
import { useEffect, useContext, useState, useMemo } from 'react';
import { useAutoScrollToError } from 'Components/frameworks/formik/ScrollToErrorHook';
import { getHealthRegulationsFromLegalInfos } from 'Components/utils/booking/bookingUtils';
import {
  HIPAY_PSP_ID,
  PAYMENT_METHOD_CATEGORY,
} from 'Components/widgets/payment-info/PaymentInfo.const';
import { isHostedFieldsPayment, isIframePayment } from 'Utils/payment/payment.utils';
import { useAppContext } from 'Context/ReactAppContext';
import { PaymentInfoContext } from 'Components/widgets/payment-info/PaymentInfoContext';
import {
  isAllowedPaymentPlan,
  isIframeBuyNowPayLaterProvider,
} from 'Components/widgets/payment-info/buy-now-pay-later/BuyNowPayLater.utils';
import { PAYMENT_PLAN_SELECTOR } from 'Components/widgets/payment-info/buy-now-pay-later/BuyNowPayLater.const';
import type { Plan } from 'Components/widgets/payment-info/payment-form/PaymentPlan';
import 'Components/widgets/payment-info/payment-form/PaymentPlan';
import { usePaypalForm } from 'Components/widgets/payment-info/payment-form/PaypalForm/usePaypalForm';
import { useBankingNavbar } from './useBankingNavbar';
import { usePaymentFormDisplay } from './usePaymentFormDisplay';
type Props = {
  i18n: I18N;
  paymentSchedule: PaymentSchedule;
  paymentProviders: ReadonlyArray<PaymentProvider>;
  buyNowPayLaterProviders: ReadonlyArray<PaymentProvider>;
  shouldShowPaymentProcess: boolean;
  totalPrice: number;
  paymentProcessOnError: boolean;
};
export const usePaymentForm = ({
  i18n,
  paymentSchedule,
  buyNowPayLaterProviders = [],
  paymentProviders,
  totalPrice,
  shouldShowPaymentProcess,
  paymentProcessOnError,
}: Props) => {
  const formik = useFormikContext();
  const appContext = useAppContext();
  const { donationAmount, isFreeDeposit, bookingResume } = useContext(PaymentInfoContext);
  const [hasFullAmount, setHasFullAmount] = useState<boolean>(
    formik.values.amountToPay === paymentSchedule.total.value,
  );
  const [loadedPaymentPlans, setLoadedPaymentPlans] = useState<
    Array<{
      id: string;
      display: boolean;
    }>
  >([]);
  usePaypalForm();
  const paymentPlans: Plan[] = buyNowPayLaterProviders
    .filter(({ id }) =>
      isAllowedPaymentPlan({
        id,
        price: totalPrice,
        startDate: bookingResume.departureDate,
      }),
    )
    .map(({ id, description }) => ({
      value: id,
      selector: PAYMENT_PLAN_SELECTOR[id],
      label: description || '',
      name: 'paymentProviderId',
      isIframe: isIframeBuyNowPayLaterProvider(id),
    }));
  const paymentInProgress = shouldShowPaymentProcess && !paymentProcessOnError;
  const selectedPaymentProvider = useMemo(
    () =>
      [...buyNowPayLaterProviders, ...paymentProviders].find(
        ({ id }) => id === formik.values.paymentProviderId,
      ),
    [formik.values.paymentProviderId],
  );
  const paymentMethod = useMemo(() => {
    return (
      selectedPaymentProvider?.paymentMethods?.find(
        ({ id }) => id === formik.values.paymentMethodId,
      ) || {
        category: '',
        id: selectedPaymentProvider?.id || '',
        image: selectedPaymentProvider?.logo || '',
        label: selectedPaymentProvider?.categoryPaymentMethod || '',
        timePaymentConditions: [],
      }
    );
  }, [selectedPaymentProvider?.id, formik.values.paymentMethodId]);
  const isHipayProvider = formik.values.paymentProviderId === HIPAY_PSP_ID;
  // in case of hosted fields we pick the paymentProduct instead
  // if the payment isHipay
  const paymentConditionId =
    (isHipayProvider && formik.values.paymentProduct) || formik.values.paymentConditionId;
  const availablePaymentPlans: Plan[] = paymentPlans.filter(({ value }) =>
    loadedPaymentPlans.find(({ id, display }) => display && id === value),
  );
  const isPaymentPlansLoading =
    !!paymentPlans.length && !(paymentPlans.length === loadedPaymentPlans.length);
  // Health Regulations Checkbox (legal_infos)
  const healthRegulations = getHealthRegulationsFromLegalInfos(i18n.bookingProcess);
  const hasHealthRegulations = !!healthRegulations;
  const isHealthRegulationsChecked =
    !hasHealthRegulations || (hasHealthRegulations && !!formik.values?.HealthRegulations?.cgv);
  const isTermsAndConditionsChecked = isEmpty(formik.errors?.termsAndConditions);
  const paymentProviderId = formik.values.paymentProviderId || '';
  const showPaymentMethodWithConditions = selectedPaymentProvider?.paymentMethods?.some(
    ({ timePaymentConditions }) => timePaymentConditions.length > 1,
  );
  const hasPaymentProvidersMatchingBlock = paymentProviders.some(({ categoryPaymentMethod }) =>
    [
      PAYMENT_METHOD_CATEGORY.PAYPAL,
      PAYMENT_METHOD_CATEGORY.DIRECT_DEBIT,
      PAYMENT_METHOD_CATEGORY.BANK_TRANSFER,
      PAYMENT_METHOD_CATEGORY.CREDIT_CARD,
    ].includes(categoryPaymentMethod),
  );
  const hasNoPaymentMethods = (selectedPaymentProvider?.paymentMethods?.length || 0) <= 1;
  const isHostedFields = isHostedFieldsPayment(paymentProviderId);
  const isIframe = isIframePayment(paymentProviderId, appContext);
  const shouldAutoSubmitForm =
    (isIframe || isHostedFields) && isHealthRegulationsChecked && !hasNoPaymentMethods;
  const selectedBuyNowPayLaterPlan = availablePaymentPlans.find(
    ({ value }) => value === paymentProviderId,
  );
  const isBuyNowPayLaterPlan = !!selectedBuyNowPayLaterPlan;
  const amountToPay = isFreeDeposit
    ? Number(formik.values.freeDeposit)
    : Number(formik.values.amountToPay);
  const totalAmountToPay = isBuyNowPayLaterPlan ? totalPrice : amountToPay;
  const shouldDisplayBNPLIframe = isTermsAndConditionsChecked && isHealthRegulationsChecked;
  const disableFields =
    paymentInProgress || !!(selectedBuyNowPayLaterPlan?.isIframe && shouldDisplayBNPLIframe);

  const setLoadedPlan = (plan: { id: string; display: boolean }) => {
    let isExistingPaymentPlan = false;
    const newLoadedPaymentPlans = [
      ...loadedPaymentPlans.map((loadedPaymentPlan) => {
        if (loadedPaymentPlan.id === plan.id) {
          isExistingPaymentPlan = true;
          return plan;
        }

        return loadedPaymentPlan;
      }),
    ];
    setLoadedPaymentPlans([...newLoadedPaymentPlans, ...(isExistingPaymentPlan ? [] : [plan])]);
  };

  useEffect(() => {
    if (!paymentProviderId) {
      const defaultPaymentProviderId = paymentProviders?.[0]?.id;
      formik.setFieldValue('paymentProviderId', defaultPaymentProviderId);
      formik.setFieldValue('selectorPaymentProviderId', defaultPaymentProviderId);
    }
  }, [paymentProviderId]);
  useAutoScrollToError(formik);
  useEffect(() => {
    // validate form on mount to make initial validation
    formik.validateForm();
  }, []);
  useEffect(() => {
    if (isIframe && hasNoPaymentMethods && formik.dirty && formik.isValid) {
      formik.submitForm();
    }
  }, [isIframe, formik.isValid, formik.values.amountToPay, hasNoPaymentMethods, formik.dirty]);
  const paymentFormDisplay = usePaymentFormDisplay({
    isBuyNowPayLaterPlan,
    paymentProviders,
    paymentPlans,
    showPaymentMethodWithConditions,
    hasPaymentProvidersMatchingBlock,
    selectedPaymentProvider,
    hasNoPaymentMethods,
    healthRegulations,
    isHealthRegulationsChecked,
    isHostedFields,
  });
  useBankingNavbar({
    selectedPaymentProvider,
    paymentPlans,
    showPaymentMethodWithConditions,
    isIframe,
    paymentMethodLabel: formik.values.paymentProduct || paymentMethod.label,
  });
  return {
    hasFullAmount,
    currentPaymentPlans: isPaymentPlansLoading ? paymentPlans : availablePaymentPlans,
    isPaymentPlansLoading,
    showPaymentMethodWithConditions,
    shouldAutoSubmitForm,
    hasPaymentProvidersMatchingBlock,
    selectedPaymentProvider,
    disableFields,
    hasNoPaymentMethods,
    healthRegulations,
    displayNewPaymentProvidersImplementation:
      hasPaymentProvidersMatchingBlock ||
      paymentFormDisplay.displayNewPaymentProvidersImplementation,
    shouldDisplayDonationTerms: !isBuyNowPayLaterPlan && !!(donationAmount && donationAmount > 0),
    shouldDisplayBNPLIframe,
    paymentConditionId,
    paymentPlans,
    paymentMethod,
    paymentInProgress,
    totalAmountToPay,
    setLoadedPlan,
    setHasFullAmount,
    paymentFormDisplay,
  };
};
